import { CSSProperties, ReactNode } from 'react';

import cx from 'classnames';
import type { GroupBase, OptionProps } from 'react-select-5';

import Icon from 'components/shared/Icon';
import Visibility from 'components/shared/Icon/icons/Visibility';

import { ContentIndicator } from '../../components/ContentIndicator';
import s from './index.module.css';

type Props<
  Option = unknown,
  IsMulti extends boolean = boolean,
  Group extends GroupBase<Option> = GroupBase<Option>,
> = OptionProps<Option, IsMulti, Group> & {
  hasPreview?: boolean;
  onPreview?: () => void;
  hasContentIndicator?: boolean;
  shared?: boolean;
  subtitle?: ReactNode;
  style?: CSSProperties;
};

const Subtitle = <
  Option = unknown,
  IsMulti extends boolean = boolean,
  Group extends GroupBase<Option> = GroupBase<Option>,
>({
  data,
  subtitle,
}: Props<Option, IsMulti, Group>) => {
  if (subtitle) {
    const title = typeof subtitle === 'string' ? subtitle : undefined;

    return <div title={title}>{subtitle}</div>;
  }

  const dataWithSubtitle = data as { subtitle?: ReactNode };

  if (dataWithSubtitle.subtitle) {
    const title = typeof dataWithSubtitle.subtitle === 'string' ? dataWithSubtitle.subtitle : undefined;

    return (
      <div className={s.additionalInfo} title={title}>
        {dataWithSubtitle.subtitle}
      </div>
    );
  }

  return null;
};

export function SafariContentOption<
  Option = unknown,
  IsMulti extends boolean = boolean,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(props: Props<Option, IsMulti, Group>) {
  const {
    children,
    innerProps,
    innerRef,
    data,
    hasPreview,
    onPreview,
    hasContentIndicator = false,
    shared,
    style,
    getClassNames,
    getStyles,
    selectProps,
  } = props;

  const getOptionLabel = selectProps.getOptionLabel || ((option) => (option as unknown as { label: string }).label);

  return (
    <div
      {...innerProps}
      ref={innerRef}
      title={getOptionLabel(data)}
      className={cx(s.option, getClassNames('option', props))}
      style={{ ...(getStyles('option', props) as CSSProperties), ...style }}
    >
      <div className={s.label}>
        <span className={s.labelTitle}>{children}</span>
        <Subtitle {...props} />
      </div>
      {hasPreview && (
        <Icon
          component={Visibility}
          width="14px"
          height="10px"
          className={s.previewIcon}
          onClick={(event) => {
            event.stopPropagation();
            onPreview?.();
          }}
        />
      )}

      {hasContentIndicator && <ContentIndicator isSharedPage={shared} />}
    </div>
  );
}
